import { CommonModule } from '@angular/common';
import { signal } from '@angular/core';
import { MaterialModule } from 'src/app/material.module';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MasterService } from 'src/app/shared/services/master.service';
import { IPersonVehicle } from '../../interfaces/person-vehicle.interface';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { IListBox } from 'src/app/shared/interfaces/IClaveValor';
import { LoaderService } from 'src/app/shared/services/loader.service';

@Component({
    selector: 'app-modal-info-vehicular',
    templateUrl: './modal-info-vehicular.component.html',
    styleUrl: './modal-info-vehicular.component.css',
    imports: [CommonModule, ReactiveFormsModule, MatDialogModule, InputTextModule, Select, StackDirectives, MaterialModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalInfoVehicularComponent implements OnInit {
    // Signal para la reactividad visual en el checkbox esPropio
    readonly esPropio = signal<boolean>(false);

    form: FormGroup;
    vehicleTypes: IListBox[] = [];
    cities: IListBox[] = [];
    loading = false;

    constructor(
        private dialogRef: MatDialogRef<ModalInfoVehicularComponent>,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder,
        private toastr: ToastrService,
        private master: MasterService,
        private loader: LoaderService,
        @Inject(MAT_DIALOG_DATA) public data: Partial<IPersonVehicle> | null
    ) {
        this.form = this.fb.group({
            id: [this.data?.id || 0],
            idTipoVehiculo: [null, Validators.required],
            marca: ['', Validators.required],
            modelo: ['', Validators.required],
            color: ['', Validators.required],
            placa: ['', Validators.required],
            esPropio: [false, Validators.required],
            identificacionPropietario: [''],
            nombresPropietario: [''],
            esPrincipal: [null], // El validador será dinámico si no existen vehiculos principales
            referencia: ['', Validators.required],
            idCiudad: [null, Validators.required],
            idDepartamento: [null]
        });
        if (data) {
            this.form.patchValue({ ...data });
            this.form.patchValue({ idTipoVehiculo: data.idTipoVehiculo?.toString() }); // Asegura que sea string para el select
            this.esPropio.set(!!this.form.get('esPropio')?.value);
            // Lógica para el validador de esPrincipal
            const vehiculos = (data as any).vehiculos ?? [];
            const tienePrincipal = vehiculos.some((v: any) => v.esPrincipal === true);
            const esPrincipalCtrl = this.form.get('esPrincipal');
            if (!vehiculos.length || !tienePrincipal) {
                esPrincipalCtrl?.setValidators([Validators.required]);
            } else {
                esPrincipalCtrl?.clearValidators();
            }
            esPrincipalCtrl?.updateValueAndValidity();
        }
    }

    ngOnInit(): void {
        this.loader.show('Cargando información...');
        this.loadVehicleTypes();

        // Escucha cambios en esPropio para marcar/limpiar validadores de propietario
        this.form.get('esPropio')?.valueChanges.subscribe((checked: boolean) => {
            this.esPropio.set(checked);
            const idPropCtrl = this.form.get('identificacionPropietario');
            const nomPropCtrl = this.form.get('nombresPropietario');
            if (!checked) {
                // No es propio: requerir datos propietario
                idPropCtrl?.setValidators([Validators.required]);
                nomPropCtrl?.setValidators([Validators.required]);
            } else {
                // Es propio: no requerir datos propietario
                idPropCtrl?.clearValidators();
                nomPropCtrl?.clearValidators();
                idPropCtrl?.setValue('');
                nomPropCtrl?.setValue('');
            }
            idPropCtrl?.updateValueAndValidity();
            nomPropCtrl?.updateValueAndValidity();
        });
        // Si viene precargado, aplica requeridos según valor
        if (!this.form.get('esPropio')?.value) {
            const idPropCtrl = this.form.get('identificacionPropietario');
            const nomPropCtrl = this.form.get('nombresPropietario');
            idPropCtrl?.setValidators([Validators.required]);
            nomPropCtrl?.setValidators([Validators.required]);
            idPropCtrl?.updateValueAndValidity();
            nomPropCtrl?.updateValueAndValidity();
        } else {
            // Es propio: limpiar validadores
            const idPropCtrl = this.form.get('identificacionPropietario');
            const nomPropCtrl = this.form.get('nombresPropietario');
            idPropCtrl?.clearValidators();
            nomPropCtrl?.clearValidators();
            idPropCtrl?.updateValueAndValidity();
            nomPropCtrl?.updateValueAndValidity();
        }
        this.cdr.markForCheck();
    }

    loadVehicleTypes() {
        forkJoin({
            vehicleTypes: this.master.getVehicleTypes().pipe(
                catchError(() => {
                    this.toastr.error('No se pudieron cargar los tipos');
                    return of(null);
                }),
                finalize(() => this.cdr.markForCheck())
            ),
            cities: this.master.getDepartmentsCities('057').pipe(
                catchError(() => {
                    this.toastr.error('No se pudieron cargar las ciudades');
                    return of(null);
                }),
                finalize(() => this.cdr.markForCheck())
            )
        }).subscribe(({ vehicleTypes, cities }) => {
            if (vehicleTypes) {
                this.vehicleTypes = vehicleTypes.data;
            }
            if (cities) {
                this.cities = cities.data;
            }
            this.loader.hide();
        });
    }

    onSave(): void {
        this.form.markAllAsTouched();
        this.cdr.markForCheck();
        if (this.form.valid) {
            let idCiudad = this.form.value.idCiudad.split('-')[0].trim();
            let idDepartamento = this.form.value.idCiudad.split('-')[1].trim();
            this.form.patchValue({ idCiudad, idDepartamento });

            const payload = {
                ...this.form.value,
                idTipoIdentificacion: 0,
                identificacion: '',
                idEstado: 0
            };
            this.dialogRef.close(payload);
        }
    }

    closeDialog(): void {
        this.dialogRef.close();
    }
}
